// Step 3 - the accounts sheet.
//
// The paste is the customer's sheet columns, tab separated: email + mailbox
// password on every platform (the API key is the whole platform identity; no
// login is borrowed from the sheet). PlusVibe and Smartlead also tolerate the
// legacy 4-column sheet, ignoring columns 3-4. Every secret goes up once through
// sqApi.setAccounts and never comes back - the item rows the server returns
// hold the email address and nothing else. The textarea is therefore never
// seeded from the server: there is nothing to seed it with, and a re-paste is a
// fresh paste from the sheet every time.
//
// A paste is all-or-nothing. A rejected one stores nothing at all, so the error
// lists below are line numbers into the box still on screen.

import { useState } from 'react';

import {
  sqApi,
  sqErrorCode,
  sqErrorMessage,
  type ApiError,
  type SqErrorCode,
  type SqRowError,
  type SqSetAccountsResult,
} from '../sequencerApi';
import type { SqStepProps } from './shared';

const TEXTAREA_ID = 'sq-accounts-text';

/** How many addresses the confirm block prints before it stops. The rest are
 *  counted, not listed: the point is to prove the right sheet was pasted, and
 *  700 rows of proof is a wall nobody reads. */
const PREVIEW_LIMIT = 50;

/** Line-numbered row errors off a rejected paste, or null when the failure
 *  carried none. `data` is a bare wire object, so nothing about its shape is
 *  trusted before it is read. */
function rowErrorsOf(err: unknown): SqRowError[] | null {
  const data = (err as ApiError).data as { rowErrors?: unknown } | undefined;
  if (!data || !Array.isArray(data.rowErrors)) return null;
  return data.rowErrors.filter(
    (r): r is SqRowError =>
      typeof r === 'object' && r !== null && typeof (r as SqRowError).line === 'number',
  );
}

/** The mailboxes another live run already owns, off an ACTIVE_IN_OTHER_RUN. */
function busyEmailsOf(err: unknown): string[] | null {
  const data = (err as ApiError).data as { emails?: unknown } | undefined;
  if (!data || !Array.isArray(data.emails)) return null;
  return data.emails.filter((e): e is string => typeof e === 'string');
}

export function StepAccounts({ runId, detail, reload, goToStep, toast }: SqStepProps): JSX.Element {
  const { run, items } = detail;

  // The paste is 2 columns everywhere; PlusVibe and Smartlead also read a
  // legacy 4-column sheet (columns 3-4 ignored). The server picks the parse
  // mode from run.sequencer; here it only changes the instructions.
  const legacyFourOk = run.sequencer === 'smartlead' || run.sequencer === 'plusvibe';

  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<SqErrorCode | null>(null);
  const [rowErrors, setRowErrors] = useState<SqRowError[] | null>(null);
  const [busyEmails, setBusyEmails] = useState<string[] | null>(null);
  const [result, setResult] = useState<SqSetAccountsResult | null>(null);

  async function parse(): Promise<void> {
    const value = text;
    if (value.trim() === '') return;
    setBusy(true);
    setError(null);
    setErrorCode(null);
    setRowErrors(null);
    setBusyEmails(null);
    setResult(null);
    try {
      const res = await sqApi.setAccounts(runId, value);
      setResult(res);
      // Cleared the moment the server has them: every line in that box holds a
      // mailbox password, and the run does not need a second copy of them in a
      // DOM node for the rest of the session.
      setText('');
      toast('success', `${res.count} account(s) saved to this run.`);
      await reload();
    } catch (err) {
      const code = sqErrorCode(err);
      setErrorCode(code);
      if (code === 'ROWS_INVALID' || code === 'NO_VALID_ACCOUNTS') {
        setRowErrors(rowErrorsOf(err));
      }
      if (code === 'ACTIVE_IN_OTHER_RUN') setBusyEmails(busyEmailsOf(err));
      setError(sqErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const preview = items.slice(0, PREVIEW_LIMIT);
  const hidden = items.length - preview.length;

  // Item rows without their passwords - a record of a purged run that cannot be
  // started; they have to be pasted again (mirror of the server's secretsGone).
  const passwordsGone = run.accounts_total > 0 && !run.has_item_passwords;

  return (
    <section className="sq-step">
      <div className="sq-step-head">
        <h2>Step 3 - Accounts</h2>
        <p>
          Copy the 2 columns straight from the Google Sheet and paste them here. Nothing is saved
          unless every row reads cleanly.
        </p>
      </div>
      <div className="sq-step-body">
        {run.accounts_total > 0 && (
          <div className={`sq-notice${passwordsGone ? ' sq-notice--warn' : ''}`}>
            <strong>{run.accounts_total} account(s)</strong> are already on this run.{' '}
            {passwordsGone
              ? 'Their passwords were purged with the run credentials, so they cannot be started as they are - paste the sheet again to restore them.'
              : 'Pasting again REPLACES the whole list: every existing row is deleted and re-created from the new paste.'}
          </div>
        )}

        <div className="sq-field">
          <label className="sq-field-label" htmlFor={TEXTAREA_ID}>
            One account per line, email then mailbox password, tab-separated
          </label>
          <div className="sq-notice sq-notice--info">
            {legacyFourOk ? (
              <>
                <strong>1.</strong> email &nbsp; <strong>2.</strong> mailbox password &nbsp; - a
                4-column sheet is accepted too; columns 3-4 are ignored.
              </>
            ) : (
              <>
                <strong>1.</strong> email &nbsp; <strong>2.</strong> email password
              </>
            )}
          </div>
          <textarea
            id={TEXTAREA_ID}
            className="sq-textarea sq-mono"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'user@acme.com\tMailboxPw1!\nsales@acme.com\tMailboxPw2!'}
            spellCheck={false}
            autoComplete="off"
            disabled={busy}
          />
          <span className="sq-hint">
            {legacyFourOk
              ? 'Paste from Google Sheets: 2 columns per row - email, mailbox password. 4-column sheets are accepted too; the old PlusVibe username/password columns are ignored. Repeated email addresses are dropped keeping the first one.'
              : 'Selecting the 2 columns in the sheet and copying gives exactly this - tabs between the columns, one row per line. Repeated email addresses are dropped keeping the first one.'}
          </span>
        </div>

        {error && (
          <div className="sq-notice sq-notice--err">
            <div>{error}</div>

            {errorCode === 'ACTIVE_IN_OTHER_RUN' && (
              <div>
                These mailboxes are mid-connect on another run. Two runs consenting the same Google
                account at once race each other, so nothing here was saved.
              </div>
            )}

            {rowErrors !== null && rowErrors.length > 0 && (
              <ul className="sq-list">
                {rowErrors.map((r) => (
                  <li key={`${r.line}-${r.message}`}>
                    <strong>Line {r.line}:</strong> {r.message}
                  </li>
                ))}
              </ul>
            )}

            {busyEmails !== null && busyEmails.length > 0 && (
              <ul className="sq-list sq-mono">
                {busyEmails.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {result && (
          <div className="sq-notice sq-notice--ok">
            <strong>{result.count} accounts</strong> will be connected.
          </div>
        )}

        {result && result.duplicates.length > 0 && (
          <div className="sq-notice sq-notice--warn">
            <div>
              <strong>Dropped as repeats ({result.duplicates.length}):</strong> the same mailbox
              appeared more than once in the paste and only the first row was kept.
            </div>
            <ul className="sq-list sq-mono">
              {result.duplicates.map((d) => (
                <li key={`${d.line}-${d.email}`}>
                  Line {d.line}: {d.email}
                </li>
              ))}
            </ul>
            <div>Reconcile these against the sheet before you continue.</div>
          </div>
        )}

        <div className="sq-actions">
          <button
            type="button"
            className="sq-btn sq-btn--primary"
            onClick={() => void parse()}
            disabled={busy || text.trim() === ''}
          >
            {busy ? 'Parsing...' : run.accounts_total > 0 ? 'Replace the account list' : 'Parse'}
          </button>
          {items.length > 0 && !passwordsGone && (
            <button
              type="button"
              className="sq-btn sq-btn--secondary"
              onClick={() => void goToStep(run.sequencer === 'warmuptool' ? 6 : 4)}
              disabled={busy}
            >
              {result ? 'Confirm accounts' : `Continue with these ${items.length} account(s)`}
            </button>
          )}
        </div>
      </div>

      {items.length > 0 && (
        <div className="sq-table-wrap">
          <table className="sq-table">
            <thead>
              <tr>
                <th className="sq-col-num">#</th>
                <th>Mailbox</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((it) => (
                <tr key={it.email}>
                  <td className="sq-col-num sq-cell-faint">{it.position + 1}</td>
                  <td className="sq-cell-mono">{it.email}</td>
                </tr>
              ))}
              {hidden > 0 && (
                <tr>
                  <td className="sq-col-num sq-cell-faint">...</td>
                  <td className="sq-cell-faint">and {hidden} more</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
